/**
 * filter.ts — TileFilter union + resolveValidTiles helper (WEB.4.UI.7)
 *
 * TileFilter is the discriminated union that determines which tiles are
 * "valid" (selectable) in the TilePicker overlay.
 *
 * resolveValidTiles is a pure function — no store reads. Caller supplies
 * all needed state slices. HexBoard receives the result as highlightedTiles[].
 */

import { FACILITIES } from "../../store/facilities.js";
import type { SeatIndex } from "../../store/game-store.js";

export type { SeatIndex };

/**
 * categoryFilter tokens from cards.json may be either FacilityType
 * (e.g. "Coal", "Oil") OR FacilityCategory (e.g. "Fossil", "Renewable").
 * Empty/undefined = all facilities allowed.
 *
 * Codex R1 HIGH 2: ignoring this filter let Climate_Protest (Fossil-only) and
 * Coal_Lobby_Influence (Coal-only) target any opponent facility.
 */
export type CategoryFilter = readonly string[] | undefined;

export type TileFilter =
  /** All 33 tiles valid (incl. Start) — user-locked 2026-05-12 */
  | { kind: "Teleport" }
  /** Tiles owned by a specific opponent, optionally constrained by categoryFilter */
  | { kind: "OpponentOwned"; opponentSeat: SeatIndex; categoryFilter?: CategoryFilter }
  /** Tiles owned by the current player */
  | { kind: "SelfOwned"; categoryFilter?: CategoryFilter }
  /** Self-owned tiles with damaged/decommissioned flag */
  | { kind: "DamagedSelf" }
  /** All 33 tiles valid */
  | { kind: "Any" };

const TILE_COUNT = 33;

/** All tile indices [0..32] */
function allTiles(): number[] {
  return Array.from({ length: TILE_COUNT }, (_, i) => i);
}

/**
 * Match a facility id ("coal", "oil", …) against a single categoryFilter
 * token (case-insensitive). The token may be a FacilityType ("Coal") matching
 * the id, OR a FacilityCategory ("Fossil") matching the category field.
 */
function facilityMatchesToken(facilityId: string, token: string): boolean {
  const def = FACILITIES.find((f) => f.id === facilityId);
  if (def === undefined) return false;
  const t = token.toLowerCase();
  if (def.id.toLowerCase() === t) return true;
  if (def.category.toLowerCase() === t) return true;
  return false;
}

/** True when the facility matches ANY token in the filter, or filter is empty. */
function facilityMatchesCategoryFilter(
  facilityId: string | undefined,
  filter: CategoryFilter,
): boolean {
  if (facilityId === undefined) return false;
  if (filter === undefined || filter.length === 0) return true;
  return filter.some((tok) => facilityMatchesToken(facilityId, tok));
}

/**
 * Compute the set of valid tile indices for a given filter.
 *
 * @param filter          — TileFilter discriminant
 * @param currentSeat     — seat index of the player making the pick
 * @param builtFacilities — tileIdx → facilityId (a facility exists on this tile)
 * @param ownedBy         — tileIdx → SeatIndex (who owns the tile)
 * @param damagedFlags    — optional: tileIdx → true when decommissioned/damaged
 */
export function resolveValidTiles(
  filter: TileFilter,
  currentSeat: SeatIndex,
  builtFacilities: Record<number, string | undefined>,
  ownedBy: Record<number, SeatIndex | undefined>,
  damagedFlags?: Record<number, boolean>,
): number[] {
  switch (filter.kind) {
    case "Teleport":
    case "Any":
      return allTiles();

    case "OpponentOwned": {
      const targetSeat = filter.opponentSeat;
      const catFilter = filter.categoryFilter;
      return Object.entries(builtFacilities)
        .filter(([tileIdxStr]) => {
          const tileIdx = Number(tileIdxStr);
          if (ownedBy[tileIdx] !== targetSeat) return false;
          return facilityMatchesCategoryFilter(builtFacilities[tileIdx], catFilter);
        })
        .map(([tileIdxStr]) => Number(tileIdxStr));
    }

    case "SelfOwned": {
      const catFilter = filter.categoryFilter;
      return Object.entries(builtFacilities)
        .filter(([tileIdxStr]) => {
          const tileIdx = Number(tileIdxStr);
          if (ownedBy[tileIdx] !== currentSeat) return false;
          return facilityMatchesCategoryFilter(builtFacilities[tileIdx], catFilter);
        })
        .map(([tileIdxStr]) => Number(tileIdxStr));
    }

    case "DamagedSelf": {
      if (damagedFlags === undefined) return [];
      return Object.entries(builtFacilities)
        .filter(
          ([tileIdxStr]) =>
            ownedBy[Number(tileIdxStr)] === currentSeat &&
            damagedFlags[Number(tileIdxStr)] === true,
        )
        .map(([tileIdxStr]) => Number(tileIdxStr));
    }
  }
}
